import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchWalletSummary, fetchWalletTransactions, type WalletTransaction } from '@/api/wallet';
import { formatBalanceAmount, formatBalanceU } from '@/utils/product-display';
import { openSaleSmartlyChat } from '@/lib/salesmartly';

const filterTabs = ['全部', '充值', '消费', '佣金', '提现'];

type QuickAction = {
  icon: string;
  label: string;
  color: string;
  path?: string;
  action?: () => void;
};

function getTransactionStatusLabel(txn: WalletTransaction) {
  const status = String(txn.status ?? '');

  if (status === '0') return '已完成';
  if (status === '1') return txn.txnType === 'withdraw' ? '提现处理中' : '处理中';
  if (status === '2') return txn.txnType === 'withdraw' ? '已驳回' : '失败';

  return '处理中';
}

export default function WalletPage() {
  const [activeFilter, setActiveFilter] = useState('全部');
  const [summary, setSummary] = useState({ balance: 0, frozen: 0, totalRecharge: 0, totalSpent: 0 });
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);

  useEffect(() => {
    fetchWalletSummary().then((res) => {
      if (res.code === 0 && res.data) {
        setSummary({
          balance: Number(res.data.balance || 0),
          frozen: Number(res.data.frozen || 0),
          totalRecharge: Number(res.data.totalRecharge || 0),
          totalSpent: Number(res.data.totalSpent || 0),
        });
      }
    });
    fetchWalletTransactions().then((res) => {
      if (res.code === 0 && Array.isArray(res.data)) {
        setTransactions(res.data);
      }
    });
  }, []);

  const filtered = transactions.filter((t) => {
    if (activeFilter === '全部') return true;
    if (activeFilter === '充值') return t.txnType === 'recharge';
    if (activeFilter === '消费') return t.txnType === 'spend' || t.txnType === 'order';
    if (activeFilter === '佣金') return t.txnType === 'commission';
    if (activeFilter === '提现') return t.txnType === 'withdraw';
    return true;
  });
  const quickActions: QuickAction[] = [
    { icon: 'ri-add-circle-line', label: '立即充值', path: '/recharge', color: 'text-emerald-600' },
    { icon: 'ri-exchange-line', label: '提现申请', path: '/withdraw', color: 'text-amber-600' },
    { icon: 'ri-customer-service-2-line', label: '在线客服', action: () => openSaleSmartlyChat(), color: 'text-gray-600' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/account" className="hover:text-gray-700 transition-colors cursor-pointer">用户中心</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">我的钱包</span>
        </div>

        {/* Balance Card */}
        <div className="bg-gray-900 rounded-2xl p-6 mb-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/4" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                  <i className="ri-wallet-3-line text-white text-lg" />
                </div>
                <span className="text-white/70 text-sm font-medium">账户余额</span>
              </div>
              <Link
                to="/recharge"
                className="flex items-center gap-1.5 px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />
                充值
              </Link>
            </div>
            <div className="mb-4 flex items-baseline gap-1">
              <span className="text-white text-4xl font-bold">{formatBalanceAmount(summary.balance)}</span>
              <span className="text-lg font-medium text-white/70">U</span>
            </div>
            <div className="grid grid-cols-1 sm:flex sm:items-center gap-3 sm:gap-6 text-sm">
              <div>
                <span className="text-white/50">冻结金额</span>
                <span className="text-white/80 ml-2 font-medium">{formatBalanceU(summary.frozen)}</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/20" />
              <div>
                <span className="text-white/50">累计充值</span>
                <span className="text-white/80 ml-2 font-medium">{formatBalanceU(summary.totalRecharge)}</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/20" />
              <div>
                <span className="text-white/50">累计消费</span>
                <span className="text-white/80 ml-2 font-medium">{formatBalanceU(summary.totalSpent)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {quickActions.map((item) => (
            item.path ? (
              <Link
                key={item.label}
                to={item.path}
                className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col items-center gap-2 hover:border-gray-200 hover:bg-gray-50 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl">
                  <i className={`${item.icon} text-xl ${item.color}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </Link>
            ) : (
              <button
                type="button"
                key={item.label}
                onClick={item.action}
                className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col items-center gap-2 hover:border-gray-200 hover:bg-gray-50 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl">
                  <i className={`${item.icon} text-xl ${item.color}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </button>
            )
          ))}
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-800">交易记录</h2>
            <span className="text-xs text-gray-400">共 {filtered.length} 条</span>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-1 p-1 bg-gray-50 rounded-xl mb-5 w-full overflow-x-auto sm:w-fit">
            {filterTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === tab
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="space-y-0">
            {filtered.map((txn, idx) => (
              <div
                key={txn.txnId}
                className={`flex items-start sm:items-center justify-between gap-3 py-4 ${idx < filtered.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    txn.txnType === 'recharge' ? 'bg-emerald-50' :
                    txn.txnType === 'commission' ? 'bg-amber-50' :
                    txn.txnType === 'withdraw' ? 'bg-orange-50' : 'bg-gray-50'
                  }`}>
                    <i className={`text-lg ${
                      txn.txnType === 'recharge' ? 'ri-arrow-down-circle-line text-emerald-500' :
                      txn.txnType === 'commission' ? 'ri-gift-line text-amber-500' :
                      txn.txnType === 'withdraw' ? 'ri-exchange-dollar-line text-orange-500' :
                      'ri-arrow-up-circle-line text-gray-400'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 break-words">{txn.description}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{txn.createTime}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-sm font-bold ${
                    Number(txn.amount) > 0 ? 'text-emerald-600' : 'text-gray-700'
                  }`}>
                    {Number(txn.amount) > 0 ? '+' : ''}{formatBalanceAmount(txn.amount)} U
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">{getTransactionStatusLabel(txn)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </main>
      <Footer />
    </div>
  );
}
